(ns app.front.view.application-wizard.classification
  "Classification step for the application wizard: category, work-type and work-target selection."
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui.action-buttons :as ui-ab]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.multi-select :as ui-ms]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.select :as ui-select]
            [app.front.util :refer [for-frag]]
            [app.front.view.application-wizard.subs :as subs]
            [app.i18n.model :as i18n-model]
            [core.front.event :refer [<sub >evt]]
            [core.front.media-query :as media-query]
            [core.front.routing :as routing]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::selected-category-id
  :-> ::category-id)

(rf/reg-sub ::selected-work-type-ids
  :-> ::work-type-ids)

(rf/reg-sub ::selected-work-target-id
  :-> ::work-target-id)

(defn location-not-required? [ctx category work-types work-target]
  (some->> (or (some-> work-target vector)
               (seq work-types)
               (some-> category vector))
           (map (partial entities/find-entity ctx))
           (every? (partial config-model/resolve-config-value :group.config/no-workspace-location))))

(defn- save-args [{:keys [category-id work-type-ids work-target-id]}]
  (when category-id
    (p/assoc-if {:category-id category-id}
                :work-type-ids (some->> work-type-ids seq set)
                :work-target-id work-target-id)))

(defn- workspace->persisted-classification
  "Returns {:category :work-types :work-target} for the workspace, or nil.
   :work-types is nil when empty."
  [workspace]
  (when workspace
    (let [application (workspace-model/workspace-application workspace)]
      {:category    (-> (or (:application/category-group-config application)
                            (:workspace/category-group-config workspace))
                        :db/id)
       :work-types  (->> (or (:application/work-type-group-configs application)
                             (:workspace/work-type-group-configs workspace))
                         (map :db/id)
                         not-empty)
       :work-target (-> (or (:application/work-target-group-config application)
                            (:workspace/work-target-group-config workspace))
                        :db/id)})))

(rf/reg-sub ::last-saved-args
  :-> ::last-saved-args)

(rf/reg-sub ::classification-change-save-args
  :<- [::subs/workspace-id]
  :<- [::entities/context]
  :<- [::selected-category-id]
  :<- [::selected-work-type-ids]
  :<- [::selected-work-target-id]
  :<- [::last-saved-args]
  (fn [[workspace-id ctx category-id work-type-ids work-target-id last-saved-args] _]
    (when-let [workspace (and workspace-id
                              (entities/find-entity ctx {:workspace/id workspace-id}))]
      (let [{:keys [category work-types work-target]} (workspace->persisted-classification workspace)
            selected-save-args                        (save-args (util/mmap category-id work-type-ids work-target-id))
            persisted-save-args                       (or last-saved-args
                                                          (save-args {:category-id    category
                                                                      :work-type-ids  work-types
                                                                      :work-target-id work-target}))]
        (when (and selected-save-args
                   (not= selected-save-args persisted-save-args))
          selected-save-args)))))


(rf/reg-sub ::classification-dirty?
  :<- [::classification-change-save-args]
  :-> some?)

(rf/reg-sub ::workspace-exists?
  :<- [::subs/workspace-id]
  :-> some?)

(rf/reg-event-fx ::save-current-classification
  (fn [{:keys [db]} _]
    (let [workspace-id  (or (-> db :route/match :parameters :path :workspace-id)
                            (-> db :route/match :parameters :query :workspace-id))
          ctx           (entities/db->context db)
          workspace     (and workspace-id
                             (entities/find-entity ctx {:workspace/id workspace-id}))
          current-args  (save-args {:category-id    (::category-id db)
                                    :work-type-ids  (::work-type-ids db)
                                    :work-target-id (::work-target-id db)})
          signal-target (and workspace current-args
                             (workspace-model/workspace-target workspace))]
      (when signal-target
        {:dispatch [::process/signal
                    signal-target
                    [:process.signal/set-ws-config-path current-args]
                    {:on-success  [::save-current-classification-success current-args]
                     :track-save? true}]}))))

(rf/reg-event-db ::save-current-classification-success
  (fn [db [_ saved-args _ _]]
    (assoc db ::last-saved-args saved-args)))

(rf/reg-event-db ::set-classification
  (fn [db [_ {:keys [category work-types work-target]}]]
    (p/assoc-if (dissoc db ::category-id ::work-type-ids ::work-target-id)
                ::category-id category
                ::work-type-ids (not-empty work-types)
                ::work-target-id work-target)))

(defn- save-args->classification [{:keys [category-id work-type-ids work-target-id]}]
  {:category    category-id
   :work-types  (some-> work-type-ids seq vec)
   :work-target work-target-id})

(rf/reg-event-fx ::reset-to-persisted
  (fn [{:keys [db]} _]
    (let [workspace-id (or (-> db :route/match :parameters :path :workspace-id)
                           (-> db :route/match :parameters :query :workspace-id))
          ctx          (entities/db->context db)
          workspace    (and workspace-id
                            (entities/find-entity ctx {:workspace/id workspace-id}))]
      (when-let [classification (or (some-> (::last-saved-args db) save-args->classification)
                                    (workspace->persisted-classification workspace))]
        {:dispatch [::set-classification classification]}))))

; UI selections update the URL query params, so they survive a refresh.
(rf/reg-event-fx ::select-category
  (fn [{:keys [db]} [_ category-id]]
    {:db       (p/assoc-if (dissoc db ::category-id ::work-type-ids ::work-target-id)
                           ::category-id category-id)
     :dispatch [::routing/merge-query-params {:category    category-id
                                              :work-types  nil
                                              :work-target nil}
                :route/replace]}))

(rf/reg-event-fx ::select-work-types
  (fn [{:keys [db]} [_ work-type-ids]]
    {:db       (p/assoc-if (dissoc db ::work-type-ids ::work-target-id)
                           ::work-type-ids (not-empty work-type-ids))
     :dispatch [::routing/merge-query-params {:work-types  (some-> work-type-ids seq vec)
                                              :work-target nil}
                :route/replace]}))

(rf/reg-event-fx ::select-work-target
  (fn [{:keys [db]} [_ work-target-id]]
    {:db       (p/assoc-if (dissoc db ::work-target-id)
                           ::work-target-id work-target-id)
     :dispatch [::routing/merge-query-params {:work-target work-target-id} :route/replace]}))

; Restores selections from the URL after a refresh. `or` keeps in-memory values,
; which are newer when route controllers re-fire during in-app navigation.
(rf/reg-event-db ::restore-from-query
  (fn [db [_ query]]
    (-> db
        (update ::category-id    #(or % (:category query)))
        (update ::work-type-ids  #(or % (not-empty (:work-types query))))
        (update ::work-target-id #(or % (:work-target query))))))

(rf/reg-event-db ::cleanup
  (fn [db _]
    (dissoc db ::category-id ::work-type-ids ::work-target-id ::last-saved-args)))

(rf/reg-sub ::multiple-work-types?
  :<- [::subs/permit-type]
  (fn [permit-type]
    (contains? ptc-model/permit-types-with-multiple-work-types permit-type)))

(defn- selectable-group-config? [authority?]
  (every-pred du/is-active?
              (partial config-model/allow-workspace-creation? authority?)))

(defn- filter-group-configs [language authority? configs]
  (->> configs
       (filter (selectable-group-config? authority?))
       (config-model/sort-group-configs language)))

(rf/reg-sub ::category-group-configs
  :<- [::entities/context]
  :<- [:language]
  :<- [::subs/permit-type-groups]
  (fn [[ctx language groups] _]
    ;; A municipality may be served by several authority groups, each with their own categories. The
    ;; user can be an authority in only some of them, so authority-only categories are filtered with
    ;; the authority status of the group that owns them.
    (->> groups
         (mapcat (fn [{:keys [category-gc-ids authority?]}]
                   (->> (entities/find-entities ctx category-gc-ids)
                        (filter (selectable-group-config? authority?)))))
         (config-model/sort-group-configs language))))

(defn child-group-configs [ctx language parent-id authority?]
  (->> (entities/find-entities ctx {:entity/parent parent-id
                                    :entity/type   :entity.type/group-config})
       (filter-group-configs language authority?)))

(rf/reg-sub ::work-type-configs
  :<- [::entities/context]
  :<- [:language]
  :<- [::selected-category-id]
  :<- [::subs/authority-user?]
  (fn [[ctx lang category-id authority?] _]
    (when category-id
      (child-group-configs ctx lang category-id authority?))))

(rf/reg-sub ::work-target-configs
  :<- [::entities/context]
  :<- [:language]
  :<- [::selected-work-type-ids]
  :<- [::subs/authority-user?]
  (fn [[ctx lang work-type-ids authority?] _]
    (when (seq work-type-ids)
      (->> work-type-ids
           (mapcat #(child-group-configs ctx lang % authority?))
           distinct))))

(rf/reg-sub ::selected-category-config
  :<- [::category-group-configs]
  :<- [::selected-category-id]
  (fn [[categories category-id] _]
    (p/find-first categories {:db/id category-id})))

(rf/reg-sub ::category-title
  :<- [::selected-category-config]
  :<- [:language]
  (fn [[selected-category-config language] _]
    (some-> selected-category-config
            :group.config/names
            (i18n-model/get-text language))))

(rf/reg-sub ::category-description
  :<- [::selected-category-config]
  :<- [:tr/quiet]
  (fn [[selected-cat-config tr-quiet] _]
    (when selected-cat-config
      (let [standard (and (nil? (:group.config/category-description selected-cat-config))
                          (some->> (:group.config/target selected-cat-config)
                                   name
                                   (keyword "category.description")
                                   vector
                                   tr-quiet))
            modified (when-let [desc (:group.config/category-description selected-cat-config)]
                       (when-not (ui-rt/empty-texts? desc)
                         desc))]
        (util/mmap standard modified)))))

(rf/reg-sub ::pre-consultation-preference
  :<- [::selected-category-config]
  (fn [selected-category-config _]
    (some-> selected-category-config
            (get :group.config/pre-consultation config-model/default-pre-consultation))))

(rf/reg-sub ::pre-consultation-selected?
  :-> ::pre-consultation-selected?)

(rf/reg-sub ::create-pre-consultation?
  :<- [::pre-consultation-preference]
  :<- [::pre-consultation-selected?]
  (fn [[preference selection] _]
    (case preference
      :group.config.pre-consultation/not-applicable false
      :group.config.pre-consultation/optional       (boolean selection)
      :group.config.pre-consultation/required       true
      false)))

(rf/reg-event-db ::select-pre-consultation
  (fn [db [_ value]]
    (assoc db ::pre-consultation-selected? value)))

(rf/reg-sub ::step-completed?
  :<- [::selected-category-id]
  :<- [::selected-work-type-ids]
  :<- [::selected-work-target-id]
  :<- [::work-type-configs]
  :<- [::work-target-configs]
  :<- [::create-pre-consultation?]
  (fn [[selected-category selected-work-types selected-work-target work-types work-targets create-pre-consultation?] _]
    (boolean
      (and selected-category
           (or create-pre-consultation?
               (and (or (empty? work-types) (seq selected-work-types))
                    (or (empty? work-targets) selected-work-target)))))))

(defn- category-list-item [{:keys [title selected? on-click]}]
  [:button.w-full.text-left.px-4.py-3.rounded.border.border-solid.transition-colors.focus:shadow-none
   {:class         (if selected?
                     "border-violet-800 border-2 bg-violet-100 font-semibold"
                     "border-gray-300 bg-white hover:border-violet-400 hover:bg-violet-50")
    :on-click      on-click
    :data-selected (when selected? true)}
   title])

(defn- category-select [{:keys [categories selected-category-id language mobile? tr]}]
  (let [on-select   #(>evt [::select-category %])
        guide-attrs {:data-guide-id          "category-select"
                     :data-guide-label       (tr [:wizard/category])
                     :data-guide-description "Select the permit category for this project"}]
    (if mobile?
      [:div guide-attrs
       [ui-select/select {:id           "classification-category-select"
                          :class-name   "w-full text-lg mb-4"
                          :label-text   (tr [:wizard/category])
                          :allow-empty? false
                          :select-text  (tr [:application-wizard/select])
                          :values       categories
                          :value        (p/find-first categories {:db/id selected-category-id})
                          :val->str     #(i18n-model/get-text (:group.config/names %) language)
                          :on-change    #(some-> % :db/id on-select)}]]
      [:div.flex.flex-col.gap-2.overflow-y-auto
       (merge {:class "max-h-[400px]"
               :ref   (fn [el]
                        (when el
                          (when-let [selected (.querySelector el "[data-selected=true]")]
                            (.scrollIntoView selected #js {:block "center"}))))}
              guide-attrs)
       (for-frag [cat  categories
                  :let [id    (:db/id cat)
                        title (i18n-model/get-text (:group.config/names cat) language)]]
         [category-list-item {:title     title
                              :selected? (= id selected-category-id)
                              :on-click  #(on-select id)}])])))

(defn- work-type-select [{:keys [work-types multiple-work-types? selected-work-type-ids language tr]}]
  (let [common-opts {:id           "classification-work-type-select"
                     :class-name   "w-full text-lg"
                     :label-text   (tr [:work-type-select])
                     :allow-empty? false
                     :select-text  (tr [:application-wizard/select])
                     :values       work-types
                     :val->str     #(i18n-model/get-text (:group.config/names %) language)}
        guide-attrs {:data-guide-id                 "work-type-select"
                     :data-guide-label              (tr [:work-type-select])
                     :data-guide-highlight-selector "select"}]
    (if multiple-work-types?
      [:div (assoc guide-attrs :data-guide-description "Select one or more specific types of work for this permit")
       [ui-ms/multi-select (merge common-opts
                                  {:value     (->> work-types
                                                   (filter #(contains? (set selected-work-type-ids) (:db/id %))))
                                   :on-change #(>evt [::select-work-types (map :db/id %)])})]]
      [:div (assoc guide-attrs :data-guide-description "Select the specific type of work for this permit")
       [ui-select/select (merge common-opts
                                {:value     (p/find-first work-types {:db/id (first selected-work-type-ids)})
                                 :on-change #(>evt [::select-work-types [(:db/id %)]])})]])))

(defn- work-target-select [{:keys [work-targets selected-work-target-id language tr]}]
  [:div {:data-guide-id                 "work-target-select"
         :data-guide-label              (tr [:work-target-select])
         :data-guide-description        "Select the target or scope of the work"
         :data-guide-highlight-selector "select"}
   [ui-select/select {:id           "classification-work-target-select"
                      :class-name   "w-full"
                      :label-text   (tr [:work-target-select])
                      :allow-empty? false
                      :select-text  (tr [:application-wizard/select])
                      :values       work-targets
                      :value        (p/find-first work-targets {:db/id selected-work-target-id})
                      :val->str     #(i18n-model/get-text (:group.config/names %) language)
                      :on-change    (fn [selected]
                                      (when-let [id (:db/id selected)]
                                        (>evt [::select-work-target id])))}]])

(defn- category-description-card [{:keys [selected-category-id category-title category-description]}]
  (let [{:keys [standard modified]} category-description]
    [:div.pt-3.pb-4.px-4
     [:h4.mb-1.font-bold category-title]
     (cond
       modified
       [ui-rt/editor {:id            (str "category-description-" selected-category-id)
                      :initial-value modified}]

       standard
       [:p.whitespace-pre-wrap.pb-0 standard])]))

(defn- pre-consultation-section [tr pre-consultation-preference create-pre-consultation?]
  (when (#{:group.config.pre-consultation/optional
           :group.config.pre-consultation/required}
         pre-consultation-preference)
    (if (= pre-consultation-preference :group.config.pre-consultation/required)
      [:div.p-4.border-t.border-solid.border-gray-300
       [:span (tr [:application-wizard/pre-consultation-mandatory-description])]]
      [:div.p-4.border-t.border-solid.border-gray-300
       [:span (tr [:application-wizard/pre-consultation-description])]
       [:div.w-48.mt-4
        [ui-cs/content-switcher
         {:items     [true false]
          :current   create-pre-consultation?
          :val->str  #(tr [(if % :yes :no)])
          :on-change #(>evt [::select-pre-consultation %])}]]
       [:div.mt-6.text-gray-800.text-sm
        (tr [:application-wizard/pre-consultation-small-print])]])))

(defn view [tr]
  (let [language                    (<sub [:language])
        mobile?                     (not (<sub [::media-query/breakpoint-matches? :md]))
        categories                  (<sub [::category-group-configs])
        selected-category-id        (<sub [::selected-category-id])
        work-types                  (<sub [::work-type-configs])
        multiple-work-types?        (<sub [::multiple-work-types?])
        selected-work-type-ids      (<sub [::selected-work-type-ids])
        work-targets                (<sub [::work-target-configs])
        selected-work-target-id     (<sub [::selected-work-target-id])
        category-title              (<sub [::category-title])
        category-description        (<sub [::category-description])
        pre-consultation-preference (<sub [::pre-consultation-preference])
        create-pre-consultation?    (<sub [::create-pre-consultation?])
        workspace-exists?           (<sub [::workspace-exists?])
        classification-dirty?       (<sub [::classification-dirty?])
        saving?                     (<sub [::process/signal-processing? :process.signal/set-ws-config-path])]
    [:div#application-wizard--classification
     {:data-guide-page-description
      "Application wizard — Classification step: Choose the permit category, work type, and work target that best describe your project. Some permit types allow multiple work types."}
     (when (seq categories)
       [:div (when-not mobile?
               {:class "flex gap-3 items-start"})
        [:div (when-not mobile?
                {:class "w-1-3 flex-shrink-0"})
         [category-select (util/mmap categories selected-category-id language mobile? tr)]]
        (when selected-category-id
          [:div.w-full.border.border-solid.border-gray-300.rounded
           [category-description-card (util/mmap selected-category-id category-title category-description)]
           (when (and (not create-pre-consultation?)
                      (or (seq work-types) (seq work-targets)))
             [:div.flex.flex-col.md:flex-row.gap-4.border-t.border-solid.border-gray-300.p-4
              [:div.flex-1
               (when (seq work-types)
                 [work-type-select (util/mmap work-types multiple-work-types? selected-work-type-ids language tr)])]
              [:div.flex-1
               (when (seq work-targets)
                 [work-target-select (util/mmap work-targets selected-work-target-id language tr)])]])
           (when workspace-exists?
             [:div.p-4.border-t.border-solid.border-gray-300.text-sm.text-gray-800.font-semibold
              (tr [:application-wizard/classification-change-notice])
              [:div.pt-4
               [ui-ab/save-cancel-buttons
                {:tr        tr
                 :size      :small
                 :loading?  saving?
                 :disabled? (or (not classification-dirty?) saving?)
                 :on-cancel #(>evt [::reset-to-persisted])
                 :on-save   #(>evt [::save-current-classification])}]]])
           [pre-consultation-section tr pre-consultation-preference create-pre-consultation?]])])]))
